import { Preferences } from "@capacitor/preferences";
import { useMutation } from "@tanstack/react-query";
import { PropsWithChildren, useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { post } from "@/api";
import { getUser, postUserUpdate } from "@/api/methods";
import type { Models } from "@/api/types";
import { useAppState } from "@/contexts/AppContext";
import { ThemeContext } from "@/contexts/ThemeContext";
import { practiceApiUrl } from "@/hooks/practiceApiUrl";
import { storage } from "@/modules/storage";
import { queryClient } from "@/pages/Root";
import { identifyFlagVisitor } from "@/services/Flags";
import Services from "@/services/Services";

import type {
  AuthState,
  FeatureState,
  FlowState,
  PlatformSelectionState,
  ProfileData,
  ReferralState,
} from "./OnboardingContext";
import { OnboardingContext } from "./OnboardingContext";

type CreateUserRequest = Models<"CreateMultiPlatformUserModel">;
type CreateUserResponse = Models<"CreateMultiPlatformUserResult">;

const loadSavedOnboardingState = () => {
  try {
    const saved = storage.getItem("onboarding_context_state");
    return saved ? JSON.parse(saved) : null;
  } catch {
    return null;
  }
};

export const OnboardingProvider = ({ children }: PropsWithChildren) => {
  const navigate = useNavigate();
  const appState = useAppState();
  const { resetTheme } = useContext(ThemeContext);
  const savedState = loadSavedOnboardingState();

  const updateUserMutation = useMutation({
    mutationFn: (profileData: Partial<ProfileData>) => postUserUpdate(profileData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getUser.key() });
    },
  });

  // Initialize grouped state
  const [flow, setFlow] = useState<FlowState>({
    isNewUser: savedState?.flow?.isNewUser ?? false,
  });

  const [auth, setAuth] = useState<AuthState>({
    phoneNumber: savedState?.auth?.phoneNumber,
    verificationCode: savedState?.auth?.verificationCode,
    isAuthenticated: savedState?.auth?.isAuthenticated ?? false,
    existingUser: undefined,
  });

  const [platformSelection, setPlatformSelection] = useState<PlatformSelectionState>({
    platformId: savedState?.platformSelection?.platformId ?? storage.getItem("platformId"),
    locationId: savedState?.platformSelection?.locationId,
    canReselectPlatform: savedState?.platformSelection?.canReselectPlatform ?? false,
  });

  const [referral, setReferral] = useState<ReferralState>({
    // Read from savedState first, then fall back to direct storage keys.
    // This ensures referral codes set via AppContext (e.g., AppsFlyer deep links)
    // are picked up even if OnboardingContext wasn't initialized at that time.
    referralCode:
      savedState?.referral?.referralCode ?? storage.getItem("referralCode") ?? undefined,
    statelessReferralCode:
      savedState?.referral?.statelessReferralCode ??
      storage.getItem("statelessReferralCode") ??
      undefined,
    checkInCode: savedState?.referral?.checkInCode,
  });

  const [profile, setProfile] = useState<ProfileData>(savedState?.profile ?? {});

  const [features, setFeatures] = useState<FeatureState>({
    hasScannedQRCode: savedState?.features?.hasScannedQRCode ?? false,
  });

  const saveOnboardingState = useCallback(() => {
    const stateToSave = {
      flow,
      auth: {
        ...auth,
        existingUser: undefined,
      },
      platformSelection,
      referral,
      profile,
      features,
    };

    try {
      storage.setItem("onboarding_context_state", JSON.stringify(stateToSave));
    } catch (error) {
      console.warn("Failed to save onboarding state:", error);
    }
  }, [flow, auth, platformSelection, referral, profile, features]);

  const clearSavedOnboardingState = useCallback(() => {
    storage.removeItem("onboarding_context_state");
  }, []);

  useEffect(() => {
    saveOnboardingState();
  }, [saveOnboardingState]);

  // Action creators for updating grouped state
  const updateFlow = useCallback((updates: Partial<FlowState>) => {
    setFlow((prev) => ({ ...prev, ...updates }));
  }, []);

  const updateAuth = useCallback((updates: Partial<AuthState>) => {
    setAuth((prev) => ({ ...prev, ...updates }));
  }, []);

  const updatePlatformSelection = useCallback((updates: Partial<PlatformSelectionState>) => {
    setPlatformSelection((prev: PlatformSelectionState) => ({
      ...prev,
      ...updates,
    }));
  }, []);

  const setPlatformId = useCallback((platformId: string) => {
    storage.setItem("platformId", platformId);
    // A location belongs to its platform, so choosing another practice drops the previous pick.
    setPlatformSelection((prev) =>
      prev.platformId === platformId ? prev : { ...prev, platformId, locationId: undefined },
    );
  }, []);

  const updateReferral = useCallback((updates: Partial<ReferralState>) => {
    setReferral((prev) => ({ ...prev, ...updates }));
  }, []);

  const updateProfileState = useCallback((updates: Partial<ProfileData>) => {
    setProfile((prev) => ({ ...prev, ...updates }));
  }, []);

  const clearProfile = useCallback(() => {
    setProfile({});
  }, []);

  const updateFeatures = useCallback((updates: Partial<FeatureState>) => {
    setFeatures((prev) => ({ ...prev, ...updates }));
  }, []);

  // Reset onboarding flow (when starting fresh with new phone number)
  const resetOnboardingFlow = useCallback(() => {
    setFlow({
      isNewUser: false,
    });
    setAuth({
      phoneNumber: undefined,
      verificationCode: undefined,
      isAuthenticated: false,
      existingUser: undefined,
    });
    setPlatformSelection({
      platformId: undefined,
      locationId: undefined,
      canReselectPlatform: false,
    });
    setReferral({
      referralCode: undefined,
      statelessReferralCode: undefined,
      checkInCode: undefined,
    });
    setProfile({});
    setFeatures({
      hasScannedQRCode: false,
    });
    clearSavedOnboardingState();
  }, [clearSavedOnboardingState]);

  const loadExistingUserData = useCallback(async () => {
    try {
      const user = await getUser();
      if (user) {
        // The sign-in layout identified a signed-out visitor and does not run again on the
        // following sign-up screens, so flags learn who the patient is here.
        identifyFlagVisitor(user, user.platform ?? null);
        setAuth((prev) => ({
          ...prev,
          existingUser: user,
          isAuthenticated: true,
        }));

        updateProfileState({
          firstName: user.firstName || undefined,
          lastName: user.lastName || undefined,
          email: user.email || undefined,
          dateOfBirth: user.dateOfBirth || undefined,
          gender: user.gender || undefined,
          signUpSource: user.signUpSource || undefined,
          referrerName: user.referrerName || undefined,
        });

        if (user.platform?.id) {
          setPlatformId(user.platform.id);
        }
        if (user.platformLocation?.id && user.completedProfile) {
          updatePlatformSelection({ locationId: user.platformLocation.id });
        }
        return user;
      } else {
        setAuth((prev) => ({
          ...prev,
          existingUser: undefined,
          isAuthenticated: false,
        }));
        return null;
      }
    } catch (error) {
      setAuth((prev) => ({
        ...prev,
        existingUser: undefined,
        isAuthenticated: false,
      }));
      return null;
    }
  }, [updateProfileState, setPlatformId, updatePlatformSelection]);

  const updateProfileAPI = useCallback(
    async (profileOverrides?: Partial<ProfileData>) => {
      if (auth.isAuthenticated && auth.existingUser) {
        const finalProfile = { ...profile, ...profileOverrides };
        await updateUserMutation.mutateAsync(finalProfile);
      }
    },
    [auth, profile, updateUserMutation],
  );

  const onOnboardingCompleted = useCallback(
    async (profileOverrides?: Partial<ProfileData>) => {
      try {
        if (profileOverrides) {
          updateProfileState(profileOverrides);
        }

        if (auth.isAuthenticated && auth.existingUser) {
          await updateProfileAPI(profileOverrides);
        } else {
          const anonymousToken = (await Preferences.get({ key: "multiPlatformVerifyToken" }))
            ?.value;

          const finalProfile = { ...profile, ...profileOverrides };
          if (!platformSelection.locationId) {
            throw new Error("A platform location must be selected before creating a user.");
          }

          const createUserRequest: CreateUserRequest = {
            platformLocationId: platformSelection.locationId,
            phoneNumber: auth.phoneNumber || undefined,
            firstName: finalProfile.firstName || undefined,
            lastName: finalProfile.lastName || undefined,
            email: finalProfile.email || undefined,
            dateOfBirth: finalProfile.dateOfBirth || undefined,
            gender: finalProfile.gender || undefined,
            signUpSource: finalProfile.signUpSource || undefined,
            referrerName: finalProfile.referrerName || undefined,
            signupReferralCode: referral.referralCode,
            statelessReferralCode:
              referral.statelessReferralCode ??
              storage.getItem("statelessReferralCode") ??
              undefined,
          };

          const response = (await post(
            "/multi-platform/user",
            createUserRequest,
            anonymousToken || undefined,
          )) as CreateUserResponse;

          const token = response?.token;
          if (!token) {
            throw new Error("Failed to create user");
          }

          await Services.auth.setToken(token);

          if (response.platformDomain) {
            appState.setApiUrl(practiceApiUrl(response.platformDomain));
            queryClient.clear();
            resetTheme();
          }

          const createdUser = await queryClient.fetchQuery({ ...getUser.keyed() });
          identifyFlagVisitor(createdUser, createdUser.platform ?? null);
        }

        // Clear saved onboarding state after successful completion
        clearSavedOnboardingState();

        // Handle navigation based on completion status and special flows
        const isGiftCardClaim = storage.getItem("isGiftCardClaim") === "true";
        if (isGiftCardClaim) {
          navigate("/signin/gift-card-redeem", { replace: true });
          return;
        }

        navigate("/signin/rewards", { replace: true });
      } catch (error) {
        console.error("[onOnboardingCompleted] Failed to complete onboarding:", error);
      }
    },
    [
      navigate,
      auth,
      profile,
      platformSelection,
      referral,
      clearSavedOnboardingState,
      appState,
      resetTheme,
      updateProfileAPI,
      updateProfileState,
    ],
  );

  return (
    <OnboardingContext.Provider
      value={{
        // Grouped State
        flow,
        auth,
        platformSelection,
        referral,
        profile,
        features,

        // Flow Actions
        updateFlow,

        // Auth Actions
        updateAuth,

        // Platform Selection Actions
        updatePlatformSelection,
        setPlatformId,

        // Referral Actions
        updateReferral,

        // Profile Actions
        updateProfileState,
        clearProfile,

        // Feature Actions
        updateFeatures,

        // Utility Actions
        loadExistingUserData,
        updateProfileAPI,
        onOnboardingCompleted,
        resetOnboardingFlow,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
};
